import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Observable, shareReplay, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { BaseApiService } from "../base/base-api.service";
import { ClientRole } from "../../../models/client-roles/client-role.model";
import { ClientRoleSerializer } from "../../../models/client-roles/client-role-serializer.class";
import { Injectable } from "@angular/core";
import { CustomErrorHandlerService } from "../custom-error-handler.service";

/**
 * Service for tenant roles (v1/clientrole).
 *
 * The list is cached for the session (roles change rarely); every mutation
 * clears the cache so the next read reflects the change.
 */
@Injectable()
export class ClientRolesService extends BaseApiService<ClientRole> {
  private static readonly ENDPOINT: string = "clientrole";

  private cachedList$: Observable<ClientRole[]> | undefined;

  constructor(httpClient: HttpClient) {
    super(
      httpClient,
      null,
      ClientRolesService.ENDPOINT,
      new ClientRoleSerializer(),
      new CustomErrorHandlerService()
    );
  }

  public listCached(): Observable<ClientRole[]> {
    if (!this.cachedList$) {
      this.cachedList$ = super.list().pipe(shareReplay(1));
    }
    return this.cachedList$;
  }

  public clearCache(): void {
    this.cachedList$ = undefined;
  }

  /**
   * Effective permission ids for the caller, resolved server-side from
   * their role(s) for the active tenant (GET v1/clientrole/effective).
   * Consumed by PermissionService (libs/shared/src/lib/services/auth) to
   * populate UI-enforcement checks.
   */
  public getEffectivePermissions(): Observable<string[]> {
    return this.httpClient
      .get<string[]>(`${this.url}/${ClientRolesService.ENDPOINT}/effective`)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          throwError((): HttpErrorResponse => this.errorHandler.tryParseError(error)))
      );
  }

  /**
   * Deletes a role. When reassignTo (a role key) is supplied, the backend
   * reassigns any members of the deleted role to it atomically (RBAC R1) —
   * replacing the previous client-side per-member reassignment loop.
   */
  public override delete(id: string, reassignTo?: string): Observable<unknown> {
    const query: string = reassignTo ? `?reassignTo=${encodeURIComponent(reassignTo)}` : "";
    return this.httpClient
      .delete(`${this.url}/${ClientRolesService.ENDPOINT}/${id}${query}`, {
        observe: "response"
      })
      .pipe(
        map(response => response.body),
        catchError((httpError: HttpErrorResponse) => throwError(() => httpError))
      );
  }
}
